import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import LaunchDarklyPlugin from "./plugins/launch-darkly";
import AxiosPlugin from "./plugins/axios";
import CoreuiVue from "@coreui/vue";
import "bootstrap/dist/css/bootstrap.min.css";
import BootstrapVue, { ToastPlugin } from "bootstrap-vue/src";
import {
  cilHome,
  cilDescription,
  cilChartLine,
  cilBarChart,
  cilMagnifyingGlass,
  cilResizeWidth,
  cilGrid,
  cilCloudDownload,
  cilUser,
  cilMoon,
  cilFilterX,
  cilFilter,
  cilListHighPriority
} from "@coreui/icons";
import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import { Integrations as TracingIntegrations } from "@sentry/tracing";

Vue.config.productionTip = false;

// initialize Sentry instrumentation in production
if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.VUE_APP_SENTRY_DSN,
    autoSessionTracking: true,
    integrations: [
      new TracingIntegrations.BrowserTracing({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tracingOrigins: ["localhost", process.env.VUE_APP_API_URL!]
      }),
      new VueIntegration({
        Vue,
        attachProps: true,
        logErrors: true,
        tracing: true,
        tracingOptions: {
          trackComponents: true
        }
      })
    ],
    tracesSampleRate: 1.0
  });
}

// use custom launchdarkly plugin
Vue.use(LaunchDarklyPlugin);

// use customized axios plugin
Vue.use(AxiosPlugin);

// use component library plugins
Vue.use(CoreuiVue);
Vue.use(ToastPlugin);
Vue.use(BootstrapVue);

// export app instance in order to use the router,
// store, and axios outside of a Vue instance
export const app = new Vue({
  router,
  store,
  icons: {
    cilHome,
    cilDescription,
    cilChartLine,
    cilBarChart,
    cilMagnifyingGlass,
    cilResizeWidth,
    cilGrid,
    cilCloudDownload,
    cilUser,
    cilMoon,
    cilFilterX,
    cilFilter,
    cilListHighPriority
  },
  render: h => h(App)
});

// global error handler for all javascript errors not
// handled by the following two error handlers
window.onerror = function(
  message: any,
  source: any,
  line: any,
  column: any,
  error: any
) {
  const string = message.toLowerCase();
  const substring = "script error";
  if (string.indexOf(substring) > -1) {
    app.$bvToast.toast("See browser console for details.", {
      title: "Script Error",
      autoHideDelay: 5000,
      solid: true,
      variant: "danger",
      appendToast: false
    });
  } else {
    const displayMessage = [
      "Message: " + message,
      "URL: " + source,
      "Line: " + line,
      "Column: " + column,
      "Error: " + JSON.stringify(error)
    ].join(" - ");

    app.$bvToast.toast(displayMessage, {
      title: "Error",
      autoHideDelay: 5000,
      solid: true,
      variant: "danger",
      appendToast: false
    });
  }
  return false;
};

// global error handler for unhandled promise rejections
window.addEventListener("unhandledrejection", function(event) {
  const promise = event.promise;
  const reason = event.reason;
  app.$bvToast.toast(`Promise: ${promise}, Reason: ${reason}`, {
    title: "Unhandled rejection",
    autoHideDelay: 5000,
    solid: true,
    variant: "danger",
    appendToast: false
  });
});

// global error handler for Vue errors
// eslint-disable-next-line @typescript-eslint/no-unused-vars
Vue.config.errorHandler = function(err: any, vm: any, info: any) {
  app.$bvToast.toast(`${err.toString()}\nInfo: ${info}`, {
    title: "Error",
    autoHideDelay: 5000,
    solid: true,
    variant: "danger",
    appendToast: false
  });
};

// mount the app
app.$mount("#app");
